import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  AiOutlineCalendar,
  AiOutlineSearch,
  AiOutlineReload,
  AiOutlineArrowRight,
} from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import api from "../../services/api";
import {
  ISO_FMT,
  fmtBR,
  getCategoryColor,
  inclusiveDaysCount,
  stripEmailDomain,
  toISO,
} from "../../components/FullCalendar/FullCalendarUtils";

const normalizeEndToInclusive = (startISO, endISO) => {
  const start = moment(startISO, ISO_FMT, true);
  const end = moment(endISO || startISO, ISO_FMT, true);

  if (!start.isValid()) return { startISO: null, endISO: null };
  if (!end.isValid()) return { startISO, endISO: startISO };

  if (end.isAfter(start, "day")) {
    return { startISO, endISO: end.clone().subtract(1, "day").format(ISO_FMT) };
  }

  return { startISO, endISO: startISO };
};

const getEventId = (event) => String(event?.id ?? event?.ID ?? event?.Id ?? "");
const getEventCategory = (event) => event?.category || event?.Category || event?.CATEGORY || "Evento";
const getEventComment = (event) => event?.comment || event?.Comment || event?.COMMENT || event?.title || event?.Title || "-";

export default function MeusEventosPage() {
  const toast = useToast();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/calendar/events");
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erro ao carregar meus eventos:", err);
      toast({
        title: "Erro ao carregar eventos",
        description: err?.response?.data?.message || err?.message || "Tente novamente.",
        status: "error",
        duration: 3500,
      });
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const rows = useMemo(() => {
    const mapped = (events || []).map((event) => {
      const rawStart = toISO(event.start || event.StartDate || event.START_DATE || event.rangeStart);
      const rawEnd = toISO(event.end || event.EndDate || event.END_DATE || event.rangeEnd) || rawStart;
      const normalized = normalizeEndToInclusive(rawStart, rawEnd);
      const category = getEventCategory(event);
      const color = event.categoryColor || event.CategoryColor || event.color || getCategoryColor(category);
      const employeeName = event.employeeName || event.employeeEmail || event.userName || "";

      return {
        id: getEventId(event),
        startISO: normalized.startISO,
        endISO: normalized.endISO,
        category,
        color,
        comment: getEventComment(event),
        employee: stripEmailDomain(employeeName),
        totalDays: normalized.startISO && normalized.endISO ? inclusiveDaysCount(normalized.startISO, normalized.endISO) : 0,
      };
    }).filter((row) => row.startISO);

    mapped.sort((a, b) => String(b.startISO).localeCompare(String(a.startISO)) || String(b.id).localeCompare(String(a.id)));
    return mapped;
  }, [events]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      const bag = `${row.category} ${row.comment} ${row.startISO} ${row.endISO} ${row.employee}`.toLowerCase();
      return bag.includes(q);
    });
  }, [query, rows]);

  return (
    <VStack align="stretch" spacing={6}>
      <Flex justify="space-between" align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }} gap={4}>
        <Box>
          <Heading size="lg" color="textPrimary" fontWeight="900">
            <HStack spacing={3}>
              <Icon as={AiOutlineCalendar} color="secondary" />
              <Text>Meus eventos</Text>
            </HStack>
          </Heading>
          <Text color="textSecondary" fontSize="sm" mt={1}>
            Todos os seus registros cadastrados no Schedule.
          </Text>
        </Box>

        <HStack spacing={3} flexWrap="nowrap">
          <InputGroup minW={{ base: "0", md: "280px" }} size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={AiOutlineSearch} color="textSecondary" />
            </InputLeftElement>
            <Input
              placeholder="Buscar evento..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              bg="surface"
              borderColor="borderPrimary"
              color="textPrimary"
              borderRadius="xl"
              _focus={{ borderColor: "secondary", boxShadow: "none" }}
            />
          </InputGroup>
          <Button leftIcon={<AiOutlineReload />} size="sm" variant="outline" onClick={loadEvents} isLoading={loading} flexShrink={0}>
            Atualizar
          </Button>
        </HStack>
      </Flex>

      <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
        <Flex align="center" gap={3} p={4} bg="surfaceRaised" borderBottom="1px solid" borderColor="borderPrimary">
          <Icon as={AiOutlineCalendar} color="secondary" boxSize="20px" />
          <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1.5px" fontWeight="800">
            Eventos registrados
          </Heading>
          {loading ? (
            <Spinner size="xs" color="secondary" ml="auto" />
          ) : (
            <Badge ml="auto" bg="secondarySubtle" color="secondary" fontWeight="800">
              {filteredRows.length}
            </Badge>
          )}
        </Flex>

        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr bg="surfaceRaised">
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Categoria</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Início</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Fim</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px" isNumeric>Dias</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Comentário</Th>
                <Th color="textSecondary" borderColor="borderPrimary" py={4} fontSize="10px" letterSpacing="1px">Agenda</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                    <Spinner size="md" color="secondary" mr={2} /> Carregando eventos...
                  </Td>
                </Tr>
              ) : filteredRows.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                    Nenhum evento encontrado.
                  </Td>
                </Tr>
              ) : (
                filteredRows.map((row) => (
                  <Tr key={`${row.id}-${row.startISO}`} _hover={{ bg: "surfaceHover" }}>
                    <Td borderColor="borderPrimary" py={3}>
                      <HStack spacing={2}>
                        <Box w="10px" h="10px" borderRadius="full" bg={row.color} flexShrink={0} />
                        <Text fontWeight="800" color="textPrimary" fontSize="sm">
                          {row.category}
                        </Text>
                      </HStack>
                    </Td>
                    <Td borderColor="borderPrimary" color="textPrimary" fontWeight="700">{fmtBR(row.startISO)}</Td>
                    <Td borderColor="borderPrimary" color="textPrimary" fontWeight="700">{fmtBR(row.endISO)}</Td>
                    <Td borderColor="borderPrimary" color="textPrimary" fontWeight="900" isNumeric>{row.totalDays}</Td>
                    <Td borderColor="borderPrimary" color="textSecondary" maxW="420px">
                      <Text noOfLines={2}>{row.comment}</Text>
                    </Td>
                    <Td borderColor="borderPrimary">
                      <Button size="xs" rightIcon={<AiOutlineArrowRight />} colorScheme="green" onClick={() => navigate(`/calendar?date=${row.startISO}`)}>
                        Ver
                      </Button>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </VStack>
  );
}